import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Checkbox } from '@/common/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { DateTimePicker } from '@/common/components/DateTimePicker'
import ImageUploadModal from '@/common/components/ImageUploadModal'
import { FileService } from '@/core/api/services/FileService'
import useMutationGetTournamentBannerUploadUrl from '@/core/api/hooks/useMutationGetTournamentBannerUploadUrl'
import useMutationCreateTournamentFromPreset from '@/modules/tournament/api/hooks/useMutationCreateTournamentFromPreset'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import type { TournamentPresetOption } from '../TournamentPresetSelector'
import type { components } from '@/schema'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

// Convert blob to WebP file
async function convertBlobToWebP(blob: Blob, quality: number): Promise<File> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')

		img.onload = () => {
			canvas.width = img.width
			canvas.height = img.height
			ctx?.drawImage(img, 0, 0)

			canvas.toBlob(
				(webpBlob) => {
					if (webpBlob) {
						const webpFile = new File([webpBlob], `banner_${Date.now()}.webp`, {
							type: 'image/webp',
						})
						resolve(webpFile)
					} else {
						reject(new Error('Failed to convert to WebP'))
					}
				},
				'image/webp',
				quality,
			)
		}

		img.onerror = () => reject(new Error('Failed to load image'))
		img.src = URL.createObjectURL(blob)
	})
}

interface TournamentPresetConfigFormProps {
	preset: TournamentPresetOption
	onSuccess: () => void
	onCancel: () => void
}

const presetConfigSchema = z
	.object({
		name: z.string().min(1, 'กรุณาระบุชื่อทัวร์นาเมนต์'),
		description: z.string().optional(),
		startDate: z.date({ required_error: 'กรุณาระบุวันเริ่มต้น' }),
		endDate: z.date({ required_error: 'กรุณาระบุวันสิ้นสุด' }),
		bannerImage: z.string().optional(),
		minTurnover: z.number().min(0, 'ยอดเทิร์นต้องไม่ติดลบ').optional(),
		minDeposit: z.number().min(0, 'ยอดฝากต้องไม่ติดลบ').optional(),
		minBetAmount: z.number().min(0, 'เดิมพันขั้นต่ำต้องไม่ติดลบ').optional(),
		maxTransactionDay: z.number().min(1, 'ต้องมากกว่า 0').optional(),
		allowedProviderIds: z.array(z.string()).optional(),
		allowedGameIds: z.array(z.string()).optional(),
		prizes: z
			.array(
				z.object({
					rank: z.number().min(1, 'อันดับต้องมากกว่า 0'),
					prizeAmount: z.number().min(0, 'รางวัลต้องไม่ติดลบ'),
				}),
			)
			.optional(),
	})
	.refine(
		(data) => {
			return data.endDate > data.startDate
		},
		{
			message: 'วันสิ้นสุดต้องมากกว่าวันเริ่มต้น',
			path: ['endDate'],
		},
	)

type PresetConfigFormData = z.infer<typeof presetConfigSchema>

export default function TournamentPresetConfigForm({
	preset,
	onSuccess,
	onCancel,
}: TournamentPresetConfigFormProps) {
	const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [pendingData, setPendingData] = useState<PresetConfigFormData | null>(
		null,
	)
	const [gameSearchQuery, setGameSearchQuery] = useState('')
	const [providerSearchQuery, setProviderSearchQuery] = useState('')

	// Fetch games with search
	const { data: gamesData } = useQueryGetGameList({
		skip: 0,
		take: 100,
		name: gameSearchQuery || undefined,
	})

	// Fetch providers with search (CASINO only)
	const { data: providersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 100,
		name: providerSearchQuery || undefined,
		gameType: 'CASINO',
	})

	const form = useForm<PresetConfigFormData>({
		resolver: zodResolver(presetConfigSchema),
		defaultValues: {
			name: '',
			description: preset.description,
			startDate: undefined,
			endDate: undefined,
			bannerImage: preset.bannerImage,
			minTurnover: preset.minTurnover,
			minDeposit: preset.minDeposit,
			minBetAmount: preset.minBetAmount,
			maxTransactionDay: preset.maxTransactionDay,
			allowedProviderIds: [],
			allowedGameIds: [],
			prizes: preset.prizes,
		},
	})

	const getPresignedUrlMutation = useMutationGetTournamentBannerUploadUrl()

	const createMutation = useMutationCreateTournamentFromPreset({
		onSuccess,
	})

	const handleImageUpload = async (imageBlob: Blob) => {
		try {
			// Convert to WebP with 75% quality
			const webpFile = await convertBlobToWebP(imageBlob, 0.75)

			const urlData = await getPresignedUrlMutation.mutateAsync({
				fileExtension: 'webp',
			})

			if (!urlData.presignedUrl || !urlData.fileUrl) {
				throw new Error('Failed to get upload URL')
			}

			await FileService.uploadFileToPresignedUrl(urlData.presignedUrl, webpFile)

			const imageUrl = urlData.fileUrl
			form.setValue('bannerImage', imageUrl)
			setUploadedImageUrl(imageUrl)
			toast.success('อัปโหลดรูปภาพสำเร็จ')
		} catch (error) {
			console.error('Failed to upload image:', error)
			toast.error('ไม่สามารถอัปโหลดรูปภาพได้')
		}
	}

	const onSubmit = (data: PresetConfigFormData) => {
		setPendingData(data)
		setShowConfirmDialog(true)
	}

	const handleConfirmCreate = () => {
		if (!pendingData) return

		createMutation.mutate({
			preset:
				preset.preset as components['schemas']['AdminCreateTournamentFromPresetDto']['preset'],
			name: pendingData.name,
			startDate: pendingData.startDate.toISOString(),
			endDate: pendingData.endDate.toISOString(),
			description: pendingData.description,
			bannerImage: pendingData.bannerImage,
			prizeOverrides: pendingData.prizes,
			minTurnoverOverride: pendingData.minTurnover,
			minDepositOverride: pendingData.minDeposit,
			minBetAmountOverride: pendingData.minBetAmount,
			allowedProviderIdsOverride:
				pendingData.allowedProviderIds &&
				pendingData.allowedProviderIds.length > 0
					? pendingData.allowedProviderIds.join(',')
					: undefined,
			allowedGameIdsOverride:
				pendingData.allowedGameIds && pendingData.allowedGameIds.length > 0
					? pendingData.allowedGameIds.join(',')
					: undefined,
		})

		setShowConfirmDialog(false)
		setPendingData(null)
	}

	return (
		<div className="grid gap-6 lg:grid-cols-2">
			{/* Form */}
			<Card>
				<CardHeader>
					<div className="flex items-start justify-between">
						<div>
							<CardTitle className="flex items-center gap-2">
								{preset.name}
								<Badge variant="outline">{preset.preset}</Badge>
							</CardTitle>
							<p className="mt-2 text-sm text-muted-foreground">
								{preset.description}
							</p>
						</div>
						<Button type="button" variant="ghost" onClick={onCancel}>
							เปลี่ยนรูปแบบ
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
							className="space-y-6"
						>
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>ชื่อทัวร์นาเมนต์</FormLabel>
										<FormControl>
											<Input
												placeholder="March 2026 Casino Championship"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											ชื่อทัวร์นาเมนต์ที่จะแสดงต่อผู้เล่น
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="flex items-center gap-2">
											รายละเอียด
											<Badge variant="secondary" className="text-xs">
												มีค่าเริ่มต้น
											</Badge>
										</FormLabel>
										<FormControl>
											<Textarea
												placeholder="ระบุรายละเอียดเพิ่มเติม (ถ้ามี)"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											คุณสามารถปรับแก้รายละเอียดนี้ได้
											หรือใช้ค่าเริ่มต้นจากเทมเพลต
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="startDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>วันที่เริ่มต้น</FormLabel>
											<FormControl>
												<DateTimePicker
													date={field.value}
													onDateSelect={field.onChange}
													placeholder="เลือกวันและเวลาเริ่มต้น"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="endDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>วันที่สิ้นสุด</FormLabel>
											<FormControl>
												<DateTimePicker
													date={field.value}
													onDateSelect={field.onChange}
													placeholder="เลือกวันและเวลาสิ้นสุด"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{/* Banner Image */}
							<FormField
								control={form.control}
								name="bannerImage"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="flex items-center gap-2">
											รูปแบนเนอร์
											<Badge variant="secondary" className="text-xs">
												มีค่าเริ่มต้น
											</Badge>
										</FormLabel>
										<FormControl>
											<div className="space-y-2">
												<ImageUploadModal
													buttonText="อัปโหลดรูปภาพ"
													buttonVariant="outline"
													aspectRatio={16 / 9}
													onImageCropped={handleImageUpload}
													className="w-full"
												/>
												{(uploadedImageUrl || field.value) && (
													<div className="rounded-lg border p-2">
														<img
															src={uploadedImageUrl || field.value}
															alt="Banner preview"
															className="h-auto w-full rounded-md object-cover"
															style={{ maxHeight: '200px' }}
														/>
													</div>
												)}
											</div>
										</FormControl>
										<FormDescription>
											อัปโหลดรูปแบนเนอร์ หรือใช้ค่าเริ่มต้นจากเทมเพลต
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="space-y-4">
								<h3 className="flex items-center gap-2 text-sm font-semibold">
									เงื่อนไขการเข้าร่วม
									<Badge variant="secondary" className="text-xs">
										มีค่าเริ่มต้น
									</Badge>
								</h3>
								<div className="grid grid-cols-2 gap-4">
									<FormField
										control={form.control}
										name="minTurnover"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ยอดเทิร์นขั้นต่ำ</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={preset.minTurnover.toString()}
														onNumberChange={(value) => field.onChange(value)}
														value={field.value ?? ''}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="minDeposit"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ยอดฝากขั้นต่ำ</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={preset.minDeposit.toString()}
														onNumberChange={(value) => field.onChange(value)}
														value={field.value ?? ''}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="minBetAmount"
										render={({ field }) => (
											<FormItem>
												<FormLabel>เดิมพันขั้นต่ำ</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={preset.minBetAmount.toString()}
														onNumberChange={(value) => field.onChange(value)}
														value={field.value ?? ''}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="maxTransactionDay"
										render={({ field }) => (
											<FormItem>
												<FormLabel>นับได้สูงสุด (ครั้ง/วัน)</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={preset.maxTransactionDay.toString()}
														onNumberChange={(value) => field.onChange(value)}
														value={field.value ?? ''}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							<div className="space-y-4">
								<h3 className="flex items-center gap-2 text-sm font-semibold">
									รางวัล
									<Badge variant="secondary" className="text-xs">
										มีค่าเริ่มต้น
									</Badge>
								</h3>
								{form.watch('prizes')?.map((_prize, index) => (
									<div key={index} className="grid grid-cols-2 gap-4">
										<FormField
											control={form.control}
											name={`prizes.${index}.rank`}
											render={({ field }) => (
												<FormItem>
													<FormLabel>อันดับที่</FormLabel>
													<FormControl>
														<Input
															type="number"
															onNumberChange={(value) => field.onChange(value)}
															value={field.value ?? ''}
															disabled
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name={`prizes.${index}.prizeAmount`}
											render={({ field }) => (
												<FormItem>
													<FormLabel>รางวัล (บาท)</FormLabel>
													<FormControl>
														<Input
															type="number"
															onNumberChange={(value) => field.onChange(value)}
															value={field.value ?? ''}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								))}
								<div className="rounded-lg bg-muted p-3">
									<p className="text-sm font-semibold">
										รวมรางวัลทั้งหมด:{' '}
										<span className="text-primary">
											฿
											{(
												form
													.watch('prizes')
													?.reduce((sum, p) => sum + (p.prizeAmount || 0), 0) ||
												0
											).toLocaleString()}
										</span>
									</p>
								</div>
							</div>

							{/* Allowed Providers and Games */}
							<div className="space-y-4">
								<h3 className="text-sm font-semibold">ข้อจำกัดเกมและค่าย</h3>

								<FormField
									control={form.control}
									name="allowedProviderIds"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ค่ายเกมที่อนุญาต</FormLabel>
											<FormControl>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															variant="outline"
															role="combobox"
															className="w-full justify-between"
														>
															<span className="truncate">
																{field.value && field.value.length > 0
																	? `เลือกแล้ว ${field.value.length} ค่าย`
																	: 'เลือกค่ายเกม (ไม่เลือก = อนุญาตทุกค่าย)'}
															</span>
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-full p-0" align="start">
														<div className="space-y-2 p-2">
															<Input
																placeholder="ค้นหาค่าย..."
																className="h-8 text-sm"
																value={providerSearchQuery}
																onChange={(e) =>
																	setProviderSearchQuery(e.target.value)
																}
															/>
															<div className="max-h-[300px] space-y-1 overflow-y-auto">
																{providersData?.data.map((provider) => (
																	<div
																		key={provider.id}
																		className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
																		onClick={() => {
																			const currentValue = field.value || []
																			const newValue = currentValue.includes(
																				provider.id,
																			)
																				? currentValue.filter(
																						(id) => id !== provider.id,
																					)
																				: [...currentValue, provider.id]
																			field.onChange(newValue)
																		}}
																	>
																		<Checkbox
																			checked={field.value?.includes(
																				provider.id,
																			)}
																			className="pointer-events-none h-4 w-4"
																		/>
																		<span className="text-xs">
																			{provider.displayName || provider.name}
																		</span>
																	</div>
																))}
																{(!providersData?.data ||
																	providersData.data.length === 0) && (
																	<div className="py-2 text-center text-xs text-muted-foreground">
																		ไม่พบค่ายเกม
																	</div>
																)}
															</div>
															{field.value && field.value.length > 0 && (
																<Button
																	variant="ghost"
																	className="w-full justify-center text-xs"
																	onClick={() => field.onChange([])}
																	type="button"
																>
																	ล้างทั้งหมด
																</Button>
															)}
														</div>
													</PopoverContent>
												</Popover>
											</FormControl>
											<FormDescription>
												เลือกค่ายที่อนุญาตให้เล่นในทัวร์นาเมนต์
												(ไม่เลือกหมายถึงอนุญาตทุกค่าย)
											</FormDescription>
											{field.value && field.value.length > 0 && (
												<div className="mt-2 flex flex-wrap gap-2">
													{field.value.map((providerId) => {
														const provider = providersData?.data.find(
															(p) => p.id === providerId,
														)
														return (
															<Badge key={providerId} variant="secondary">
																{provider?.displayName ||
																	provider?.name ||
																	providerId}
																<button
																	type="button"
																	className="ml-1 hover:text-destructive"
																	onClick={() => {
																		const newValue = field.value?.filter(
																			(id) => id !== providerId,
																		)
																		field.onChange(newValue)
																	}}
																>
																	<X className="h-3 w-3" />
																</button>
															</Badge>
														)
													})}
												</div>
											)}
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="allowedGameIds"
									render={({ field }) => (
										<FormItem>
											<FormLabel>เกมที่อนุญาต</FormLabel>
											<FormControl>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															variant="outline"
															role="combobox"
															className="w-full justify-between"
														>
															<span className="truncate">
																{field.value && field.value.length > 0
																	? `เลือกแล้ว ${field.value.length} เกม`
																	: 'เลือกเกม (ไม่เลือก = อนุญาตทุกเกม)'}
															</span>
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-full p-0" align="start">
														<div className="space-y-2 p-2">
															<Input
																placeholder="ค้นหาเกม..."
																className="h-8 text-sm"
																value={gameSearchQuery}
																onChange={(e) =>
																	setGameSearchQuery(e.target.value)
																}
															/>
															<div className="max-h-[300px] space-y-1 overflow-y-auto">
																{gamesData?.data.map((game) => (
																	<div
																		key={game.id}
																		className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
																		onClick={() => {
																			const currentValue = field.value || []
																			const newValue = currentValue.includes(
																				game.id,
																			)
																				? currentValue.filter(
																						(id) => id !== game.id,
																					)
																				: [...currentValue, game.id]
																			field.onChange(newValue)
																		}}
																	>
																		<Checkbox
																			checked={field.value?.includes(game.id)}
																			className="pointer-events-none h-4 w-4"
																		/>
																		<span className="text-xs">
																			{game.displayName || game.name}{' '}
																			<span className="text-muted-foreground">
																				({game.providerName})
																			</span>
																		</span>
																	</div>
																))}
																{(!gamesData?.data ||
																	gamesData.data.length === 0) && (
																	<div className="py-2 text-center text-xs text-muted-foreground">
																		ไม่พบเกม
																	</div>
																)}
															</div>
															{field.value && field.value.length > 0 && (
																<Button
																	variant="ghost"
																	className="w-full justify-center text-xs"
																	onClick={() => field.onChange([])}
																	type="button"
																>
																	ล้างทั้งหมด
																</Button>
															)}
														</div>
													</PopoverContent>
												</Popover>
											</FormControl>
											<FormDescription>
												เลือกเกมที่อนุญาตให้เล่นในทัวร์นาเมนต์
												(ไม่เลือกหมายถึงอนุญาตทุกเกม)
											</FormDescription>
											{field.value && field.value.length > 0 && (
												<div className="mt-2 flex flex-wrap gap-2">
													{field.value.map((gameId) => {
														const game = gamesData?.data.find(
															(g) => g.id === gameId,
														)
														return (
															<Badge key={gameId} variant="secondary">
																{game?.displayName || game?.name || gameId}{' '}
																{game?.providerName && (
																	<span className="text-xs">
																		({game.providerName})
																	</span>
																)}
																<button
																	type="button"
																	className="ml-1 hover:text-destructive"
																	onClick={() => {
																		const newValue = field.value?.filter(
																			(id) => id !== gameId,
																		)
																		field.onChange(newValue)
																	}}
																>
																	<X className="h-3 w-3" />
																</button>
															</Badge>
														)
													})}
												</div>
											)}
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
								<p className="text-sm text-blue-800 dark:text-blue-200">
									<span className="font-semibold">💡 คำแนะนำ:</span>{' '}
									คุณสามารถปรับแก้ค่าต่างๆ ได้ตามต้องการ
									ค่าที่แสดงเป็นค่าเริ่มต้นจากเทมเพลต{' '}
									<strong>{preset.name}</strong>
								</p>
							</div>

							<div className="flex justify-end gap-3">
								<Button type="button" variant="outline" onClick={onCancel}>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={createMutation.isPending}>
									{createMutation.isPending
										? 'กำลังสร้าง...'
										: 'สร้างทัวร์นาเมนต์'}
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			{/* Preset Preview */}
			<div className="lg:sticky lg:top-6 lg:self-start">
				<Card>
					<CardHeader>
						<CardTitle className="text-base">ตัวอย่างการตั้งค่า</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Banner Image */}
						{form.watch('bannerImage') && (
							<div className="rounded-lg border p-2">
								<img
									src={form.watch('bannerImage')}
									alt={preset.name}
									className="h-auto w-full rounded-md object-cover"
								/>
							</div>
						)}

						{/* Preset Details */}
						<div className="space-y-3">
							<div className="rounded-lg bg-muted p-3">
								<p className="text-xs font-medium text-muted-foreground">
									เงื่อนไขการเข้าร่วม
								</p>
								<div className="mt-2 space-y-1">
									<p className="text-sm">
										• ยอดเทิร์นขั้นต่ำ:{' '}
										<span className="font-medium">
											฿
											{(
												form.watch('minTurnover') ?? preset.minTurnover
											).toLocaleString()}
										</span>
									</p>
									<p className="text-sm">
										• ยอดฝากขั้นต่ำ:{' '}
										<span className="font-medium">
											฿
											{(
												form.watch('minDeposit') ?? preset.minDeposit
											).toLocaleString()}
										</span>
									</p>
									<p className="text-sm">
										• เดิมพันขั้นต่ำ:{' '}
										<span className="font-medium">
											฿
											{(
												form.watch('minBetAmount') ?? preset.minBetAmount
											).toLocaleString()}
										</span>
									</p>
									<p className="text-sm">
										• นับได้สูงสุด:{' '}
										<span className="font-medium">
											{form.watch('maxTransactionDay') ??
												preset.maxTransactionDay}{' '}
											ครั้ง/วัน
										</span>
									</p>
								</div>
							</div>

							<div className="rounded-lg bg-muted p-3">
								<p className="text-xs font-medium text-muted-foreground">
									รางวัล
								</p>
								<div className="mt-2 space-y-1">
									{(form.watch('prizes') ?? preset.prizes).map((prize) => (
										<p key={prize.rank} className="text-sm">
											• อันดับที่ {prize.rank}:{' '}
											<span className="font-medium">
												฿{prize.prizeAmount.toLocaleString()}
											</span>
										</p>
									))}
									<div className="mt-2 border-t pt-2">
										<p className="text-sm font-semibold">
											รวม: ฿
											{(form.watch('prizes') ?? preset.prizes)
												.reduce((sum, p) => sum + p.prizeAmount, 0)
												.toLocaleString()}
										</p>
									</div>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Confirmation Dialog */}
			<AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการสร้างทัวร์นาเมนต์</AlertDialogTitle>
						<AlertDialogDescription className="space-y-2">
							<p>
								คุณกำลังจะสร้างทัวร์นาเมนต์ <strong>{pendingData?.name}</strong>
							</p>
							<p className="font-semibold text-destructive">
								⚠️ หมายเหตุ: เมื่อสร้างทัวร์นาเมนต์แล้วจะไม่สามารถแก้ไขได้
							</p>
							<p>กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนยืนยัน</p>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmCreate}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending
								? 'กำลังสร้าง...'
								: 'ยืนยันสร้างทัวร์นาเมนต์'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
